import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, MoreVertical, Pencil, Copy, Trash2, Palette, Type, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useWorkspace } from '@/context/WorkspaceContext';
import { designSystemService, type DesignSystem as DesignSystemType } from '@/lib/designSystemService';
import { toast } from 'sonner';

interface DesignSystemProps {
  isGlobalDarkMode: boolean;
}

export const DesignSystem: React.FC<DesignSystemProps> = () => {
  const { currentWorkspace } = useWorkspace();
  const navigate = useNavigate();
  const [designSystems, setDesignSystems] = useState<DesignSystemType[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [dsToDelete, setDsToDelete] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    loadDesignSystems();
  }, [currentWorkspace]);

  const loadDesignSystems = async () => {
    if (!currentWorkspace) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const data = await designSystemService.getByWorkspace(currentWorkspace.id);
      setDesignSystems(data);
    } catch (error) {
      console.error('Error loading design systems:', error);
      toast.error('Failed to load design systems');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!currentWorkspace || !newName.trim()) return;
    try {
      setCreating(true);
      const ds = await designSystemService.create({
        workspace_id: currentWorkspace.id,
        name: newName.trim(),
      });
      setCreateDialogOpen(false);
      setNewName('');
      toast.success('Design system created');
      navigate(`/brand/design-system/${ds.id}/colors`);
    } catch (error) {
      console.error('Error creating design system:', error);
      toast.error('Failed to create design system');
    } finally {
      setCreating(false);
    }
  };

  const handleDuplicate = async (ds: DesignSystemType) => {
    try {
      await designSystemService.duplicate(ds.id);
      toast.success(`Duplicated "${ds.name}"`);
      loadDesignSystems();
    } catch (error) {
      console.error('Error duplicating:', error);
      toast.error('Failed to duplicate');
    }
  };

  const handleDelete = async () => {
    if (!dsToDelete) return;
    try {
      await designSystemService.delete(dsToDelete);
      toast.success('Design system deleted');
      loadDesignSystems();
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error('Failed to delete');
    } finally {
      setDeleteDialogOpen(false);
      setDsToDelete(null);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (loading) {
    return null;
  }

  return (
    <div className="w-full px-4 sm:px-6 md:px-10 py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Design Systems</h1>
          <p className="text-muted-foreground mt-1">
            Manage your CSS framework configurations — colors, typography, and more
          </p>
        </div>
        <Button
          onClick={() => { setNewName(''); setCreateDialogOpen(true); }}
          className="gap-2 bg-workspace-accent hover:bg-workspace-accent/90 text-white"
        >
          <Plus className="h-4 w-4" />
          New Design System
        </Button>
      </div>

      {/* Empty State */}
      {designSystems.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 gap-6">
          <div className="w-20 h-20 rounded-2xl bg-muted flex items-center justify-center">
            <Palette className="h-10 w-10 text-muted-foreground" />
          </div>
          <div className="text-center space-y-2 max-w-md">
            <h2 className="text-xl font-semibold text-foreground">No design systems yet</h2>
            <p className="text-muted-foreground">
              Create your first design system to define colors, typography, and more for your projects
            </p>
          </div>
          <Button
            onClick={() => { setNewName(''); setCreateDialogOpen(true); }}
            size="lg"
            className="gap-2 bg-workspace-accent hover:bg-workspace-accent/90 text-white"
          >
            <Plus className="h-4 w-4" />
            Create Design System
          </Button>
        </div>
      ) : (
        /* Card Grid */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {designSystems.map((ds) => {
            const colors = ds.color_preset?.colors || [];
            const headingFont = ds.typography_preset?.heading_font_family || '—';
            const bodyFont = ds.typography_preset?.body_font_family || '—';

            return (
              <Card
                key={ds.id}
                className="group hover:shadow-lg transition-all duration-200 cursor-pointer border border-border rounded-2xl bg-card"
                onClick={() => navigate(`/brand/design-system/${ds.id}/colors`)}
              >
                <CardContent className="p-0">
                  {/* Color Palette Preview Strip */}
                  <div className="h-10 flex rounded-t-lg overflow-hidden">
                    {colors.length > 0 ? (
                      colors.slice(0, 8).map((color: any, i: number) => (
                        <div
                          key={i}
                          className="flex-1 h-full"
                          style={{ backgroundColor: color.hex || '#6366F1' }}
                        />
                      ))
                    ) : (
                      <div className="flex-1 h-full bg-gradient-to-r from-muted to-muted-foreground/20" />
                    )}
                  </div>

                  <div className="p-5">
                    {/* Header Row */}
                    <div className="flex items-start justify-between mb-4">
                      <h3 className="font-semibold text-lg text-foreground truncate pr-2">
                        {ds.name}
                      </h3>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                          >
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/brand/design-system/${ds.id}/colors`);
                          }}>
                            <Pencil className="h-4 w-4" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={(e) => {
                            e.stopPropagation();
                            handleDuplicate(ds);
                          }}>
                            <Copy className="h-4 w-4" />
                            Duplicate
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            destructive
                            onClick={(e) => {
                              e.stopPropagation();
                              setDsToDelete(ds.id);
                              setDeleteDialogOpen(true);
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>

                    {/* Typography Info */}
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground flex items-center gap-1.5">
                          <Type className="h-3.5 w-3.5" />
                          Heading Font
                        </span>
                        <span className="font-medium text-foreground">{headingFont}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground flex items-center gap-1.5">
                          <Type className="h-3.5 w-3.5" />
                          Body Font
                        </span>
                        <span className="font-medium text-foreground">{bodyFont}</span>
                      </div>
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-between pt-3 border-t border-border/50">
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {formatDate(ds.created_at)}
                      </span>
                      <div className="flex items-center gap-1.5">
                        {colors.length > 0 && (
                          <span className="text-xs text-muted-foreground">
                            {colors.length} color{colors.length !== 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New Design System</DialogTitle>
            <DialogDescription>
              Create a new design system to define colors, typography, and more.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Design System Name"
              onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              disabled={creating || !newName.trim()}
              className="bg-workspace-accent hover:bg-workspace-accent/90 text-white"
            >
              {creating ? 'Creating...' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Design System</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this design system and its linked typography and color presets. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
